/**
 * 個人レポート v2 モバイル版。
 *
 * 正典: apps/web/docs/新方針20260821/仕様_GB人財レポートの生成ロジック.html
 *       apps/web/docs/新方針20260821/GB人財_個人レポート文言DB_v4_3.json
 *
 * 【方針（2026-08-22）】組織レポート v3 のモバイル版と同じ考え方で作る。
 *  スマホからの参照なので詳細な解説は置かず、各セクションの要点を順に見せる。
 *  算出・文言はPC（PDF: Gb74PersonalReportPdf）とまったく同じモジュールを使う
 *  （判定が1箇所なので、PCとモバイルで結論が食い違うことは起きない）。変えるのは見せ方だけ。
 *
 * 【PC(PDF)との差】
 *  ・A4横2ページの割り付けをやめ、縦スクロールの5セクションにする
 *  ・「役割適性について」など読み方の説明ページは出さない
 *  ・役割の説明文は上位2件まで（PDFは3件）
 */
import React from 'react';
import type { AxisKey } from '../utils/gb74Report';
import { AXIS_DOMINANT, AXIS_STRONG, type Gb74ReportV2 } from '../utils/gb74ReportV2';
import { getComplementSelf, type PersonalTextsV2 } from '../utils/personalTextsV2';
import { PERSONAL_REPORT_NOTES, ROLE_DESCRIPTIONS } from '../utils/personalReportCopy';
import {
  circlePath,
  polar,
  DIAMOND_AXIS_LABEL,
  DIAMOND_DEG,
  DIAMOND_ORDER,
} from '../utils/diamondGeometry';
import './Gb74PersonalReportMobile.scss';

const BLUE = '#006ABD';

/**
 * 本人のダイヤモンド（4軸・0〜12）。
 *
 * 組織版と違い、優勢の目安(7点)に基準線を引く。タイプコードがどこから来ているかが
 * 一目で分かるようにするため（7点以上の軸がコードになる）。
 */
const Diamond: React.FC<{ axis: Record<AxisKey, number> }> = ({ axis }) => {
  const VB = 260;
  const c = VB / 2;
  const R = 82;
  const at = (k: AxisKey, v: number) =>
    polar(DIAMOND_DEG[k], (Math.max(0, Math.min(12, v)) / 12) * R, c);
  const poly = DIAMOND_ORDER.map((k) =>
    at(k, axis[k])
      .map((n) => n.toFixed(1))
      .join(',')
  ).join(' ');
  // 優勢の目安(7点)のライン。ひし形で引く（軸上の7点を結ぶ）
  const baseline = DIAMOND_ORDER.map((k) =>
    polar(DIAMOND_DEG[k], (AXIS_DOMINANT / 12) * R, c)
      .map((n) => n.toFixed(1))
      .join(',')
  ).join(' ');
  return (
    <svg
      viewBox={`0 0 ${VB} ${VB}`}
      style={{ display: 'block', width: '100%', maxWidth: 250, margin: '0 auto' }}
    >
      {[0.25, 0.5, 0.75, 1].map((f) => (
        <path key={f} d={circlePath(c, c, R * f)} fill="none" stroke="#D8E4EF" />
      ))}
      {DIAMOND_ORDER.map((k) => {
        const [x, y] = polar(DIAMOND_DEG[k], R, c);
        return <line key={k} x1={c} y1={c} x2={x.toFixed(1)} y2={y.toFixed(1)} stroke="#D8E4EF" />;
      })}
      <polygon
        points={baseline}
        fill="none"
        stroke="#F0A500"
        strokeWidth="1"
        strokeDasharray="3 3"
      />
      <polygon
        points={poly}
        fill="rgba(0,106,189,0.18)"
        stroke={BLUE}
        strokeWidth="2.2"
        strokeLinejoin="round"
      />
      {DIAMOND_ORDER.map((k) => {
        const [x, y] = at(k, axis[k]);
        return <path key={k} d={circlePath(x, y, 4)} fill={BLUE} />;
      })}
      {DIAMOND_ORDER.map((k) => {
        const [x, y] = polar(DIAMOND_DEG[k], R + 26, c);
        return (
          <text
            key={k}
            x={x.toFixed(1)}
            y={y.toFixed(1)}
            fill={BLUE}
            fontSize="11"
            fontWeight="700"
            textAnchor="middle"
            dominantBaseline="middle"
          >
            {DIAMOND_AXIS_LABEL[k]} {axis[k]}
          </text>
        );
      })}
    </svg>
  );
};

/** 持ち味の出方1本（1〜5・中央3に基準線）。 */
const TraitBar: React.FC<{ name: string; value: number; emphasis: boolean }> = ({
  name,
  value,
  emphasis,
}) => {
  const pct = Math.max(0, Math.min(100, ((value - 1) / 4) * 100));
  return (
    <div className={`gb74pm-tr${emphasis ? ' _em' : ''}`}>
      <span className="n">{name}</span>
      <span className="t">
        <span className="mid" />
        <span className="f" style={{ width: `${pct}%` }} />
      </span>
      <span className="v">{value.toFixed(1)}</span>
    </div>
  );
};

/**
 * 役割1件。点数は出さずバーだけにする（仕様書04節）。
 * 役割スコアは個人内で相対化した値で、他者比較の基準がないため。
 */
const RoleBar: React.FC<{ name: string; score: number; top: boolean }> = ({ name, score, top }) => (
  <div className={`gb74pm-role${top ? ' _top' : ''}`}>
    <span className="n">{name}</span>
    <span className="t">
      <span className="f" style={{ width: `${Math.max(2, score)}%` }} />
    </span>
  </div>
);

export interface Gb74PersonalReportMobileProps {
  report: Gb74ReportV2;
  texts: PersonalTextsV2;
  userName?: string;
  answerDate?: string;
  departmentName?: string;
}

export const Gb74PersonalReportMobile: React.FC<Gb74PersonalReportMobileProps> = ({
  report,
  texts,
  userName,
  answerDate,
  departmentName,
}) => {
  const top = report.roles.filter((r) => r.top);
  const rest = report.roles.filter((r) => !r.top);

  return (
    <div className="gb74pm">
      <div className="gb74pm-bar">
        <span className="lg">GB人財 レポート</span>
        <span>{answerDate}</span>
      </div>

      <div className="gb74pm-body">
        <div className="gb74pm-hd1">GB人財 ベーシック</div>
        <div className="gb74pm-hd2">
          {userName ? `${userName} さん` : 'あなたのレポート'}
          {departmentName && <span>{departmentName}</span>}
        </div>

        {/* 01 あなたのタイプ */}
        <div className="gb74pm-sec">
          <div className="gb74pm-sn">01</div>
          <div className="gb74pm-st">あなたのタイプ</div>
          <div className="gb74pm-sq">4つの持ち味のうち、強く出ているもの</div>
        </div>
        <div className="gb74pm-card">
          <div className="gb74pm-type">
            <p className="lb">GBタイプ</p>
            <p className="ty">{report.typeCode}</p>
            {report.strongAxes.length > 0 && (
              <span className="chip">
                {report.strongAxes.join('・')} は目盛りを超えるほど強く出ています
              </span>
            )}
          </div>
          <Diamond axis={report.axis} />
          <p className="gb74pm-note">
            ※ 4軸の合計は24点で固定です。点線は「強く出ている」の目安（{AXIS_DOMINANT}点）。
            {AXIS_STRONG}点以上はとくに強く出ている軸です。
          </p>
        </div>
        {texts.typeLead && <div className="gb74pm-lead">{texts.typeLead.本文}</div>}

        {/* 02 あなたの持ち味の出方 */}
        <div className="gb74pm-sec">
          <div className="gb74pm-sn">02</div>
          <div className="gb74pm-st">あなたの持ち味の出方</div>
          <div className="gb74pm-sq">4つの力に優劣はありません。出る場面が変わります</div>
        </div>
        <div className="gb74pm-card">
          {report.traits.map((t) => (
            <TraitBar
              key={t.key}
              name={t.key}
              value={t.value}
              emphasis={t.key === report.traitTop || t.key === report.traitLow}
            />
          ))}
          <div className="gb74pm-scale">
            <span>1</span>
            <span>3</span>
            <span>5</span>
          </div>
        </div>
        {texts.traitTop && (
          <div className="gb74pm-note-card">
            <div className="ttl">いちばん出ている {report.traitTop}</div>
            <div className="txt">{texts.traitTop.本文}</div>
          </div>
        )}
        {texts.traitLow && (
          <div className="gb74pm-note-card">
            <div className="ttl">いちばん控えめ {report.traitLow}</div>
            <div className="txt">{texts.traitLow.本文}</div>
          </div>
        )}

        {/* 03 考え方と行動のスタイル */}
        {(texts.thought || texts.action) && (
          <>
            <div className="gb74pm-sec">
              <div className="gb74pm-sn">03</div>
              <div className="gb74pm-st">考え方と行動のスタイル</div>
              <div className="gb74pm-sq">どう考え、どう動くか</div>
            </div>
            {texts.thought && (
              <div className="gb74pm-card">
                <div className="gb74pm-ct">{texts.thought.見出し ?? '考え方'}</div>
                <div className="gb74pm-cb">{texts.thought.本文}</div>
              </div>
            )}
            {texts.action && (
              <div className="gb74pm-card">
                <div className="gb74pm-ct">{texts.action.見出し ?? '行動'}</div>
                <div className="gb74pm-cb">{texts.action.本文}</div>
              </div>
            )}
          </>
        )}

        {/* 04 あなたが力を出せる役割 */}
        <div className="gb74pm-sec">
          <div className="gb74pm-sn">04</div>
          <div className="gb74pm-st">あなたが力を出せる役割</div>
          <div className="gb74pm-sq">点数ではなく、あなたの中での出やすさです</div>
        </div>
        <div className="gb74pm-card">
          <div className="gb74pm-rg">◆ 力が出る役割</div>
          {top.map((r) => (
            <RoleBar key={r.name} name={r.name} score={r.score} top />
          ))}
          {rest.length > 0 && (
            <>
              <div className="gb74pm-rg _sub">— 以下は参考です</div>
              {rest.map((r) => (
                <RoleBar key={r.name} name={r.name} score={r.score} top={false} />
              ))}
            </>
          )}
        </div>
        {/* 説明は上位2件まで（スマホでは読み切れる量に絞る） */}
        {top.slice(0, 2).map((r) =>
          texts.roleBands[r.name] ? (
            <div className="gb74pm-note-card" key={`d-${r.name}`}>
              <div className="ttl">{r.name}</div>
              <div className="txt">{texts.roleBands[r.name]?.本文}</div>
            </div>
          ) : null
        )}
        {/* 6つの役割の一覧。PC(PDF)の2ページ目左下と同じ内容・同じ文言DB(PA05)。
            スマホでは横に並べると読めないので、役割ごとに縦に積む。 */}
        <div className="gb74pm-card">
          <p className="gb74pm-roles-lead">{PERSONAL_REPORT_NOTES.roleLead}</p>
          {ROLE_DESCRIPTIONS.map((r) => (
            <div className="gb74pm-roledef" key={r.name}>
              <p className="n">{r.name}</p>
              <p className="s">{r.summary}</p>
              <p className="d">{r.detail}</p>
            </div>
          ))}
        </div>

        {texts.roleReasonTop && (
          <div className="gb74pm-reason">
            {report.roleReasonTop}が高いのは、{texts.roleReasonTop.本文}
          </div>
        )}
        {texts.roleReasonLow && (
          <div className="gb74pm-reason">
            {report.roleReasonLow}が低いのは、{texts.roleReasonLow.本文}
          </div>
        )}

        {/* 05 あなたを輝かせるには？ */}
        <div className="gb74pm-sec">
          <div className="gb74pm-sn">05</div>
          <div className="gb74pm-st">あなたを輝かせるには？</div>
          <div className="gb74pm-sq">伸ばすところと、気をつけるところ</div>
        </div>
        <div className="gb74pm-shine _good">
          <div className="ttl">◆ 伸ばすところ</div>
          <div className="txt">
            {texts.thought?.本文}
            {texts.action?.本文 ? ` ${texts.action.本文}` : ''}
            {report.topRoles[0] && <> この力は「{report.topRoles[0]}」の場面でいちばん出ます。</>}
          </div>
        </div>
        {texts.caution && (
          <div className="gb74pm-shine _care">
            <div className="ttl">◇ 気をつけるところ</div>
            <div className="txt">{texts.caution.本文}</div>
          </div>
        )}
        <div className="gb74pm-shine _fix">
          <div className="ttl">{texts.complement?.見出し ?? '補い方'}</div>
          <div className="txt">{getComplementSelf(report.lowestAxis)}</div>
        </div>

        <div className="gb74pm-foot">
          本レポートは持ち味を可視化するものであり、能力の優劣を評価するものではありません。
        </div>
      </div>
    </div>
  );
};

export default Gb74PersonalReportMobile;
